import { create } from "zustand";
import { FusawareUser } from "../fusawareUser";
import { FusaToast, FusaToastType } from "../toasts/types";
import { CopiedItem } from "@/types/copyPasteTypes";
import { ConfirmFileUploadResponse, CreateFileRequest, CreateFileResponse, GetFileDownloadUrlResponse } from "@/backend/express/controllers/fileController";
import { FileController } from "../controllers/fileController";
import { UpdatedParamsAndRefs } from "./TreeStore";
import { I18n } from "@/i18n/I18n";
import { downloadFileFromS3 } from "../utils/downloadFileFromS3";
import { File as FileModel } from "@/backend/models/file";
import { Enterprise } from "@/backend/models/enterprise";
import { UserParams } from "@/backend/access/user/types";
import { EnterpriseController } from "../controllers/enterpriseController";
import { UserController } from "../controllers/userController";
import { OrgMemberInfoClass, UserClass } from "@propelauth/react";
import { WhoamiController, WhoamiUser } from "../controllers/whoamiController";

/**
 * What `authenticateWithToken` concluded about the token it was given.
 *
 * `unauthorized` and `error` are kept apart on purpose: the first means go and
 * get a new token, the second means the API could not be reached and asking for
 * a new one would neither help nor be safe -- a blip would sign the user out.
 */
export type AuthenticateResult = "ok" | "unauthorized" | "error";

function toFusawareUser(user: WhoamiUser): FusawareUser {
  const orgIdToUserOrgInfo: Record<string, OrgMemberInfoClass> = {};
  Object.entries(user.orgIdToOrgMemberInfo ?? {}).forEach(([orgId, org]) => {
    orgIdToUserOrgInfo[orgId] = new OrgMemberInfoClass(
      org.orgId,
      org.orgName,
      org.orgMetadata,
      org.urlSafeOrgName,
      org.userAssignedRole,
      org.userInheritedRolesPlusCurrentRole,
      org.userPermissions,
    );
  });

  return new FusawareUser(
    new UserClass(
      {
        userId: user.userId,
        email: user.email,
        createdAt: 0,
        firstName: user.firstName,
        lastName: user.lastName,
      },
      orgIdToUserOrgInfo,
    ),
  );
}

const initialState = {
  accessToken: '',
  userClass: null,
  redirectToLogin: null,
  toasts: [],
  copiedItem: null,
  enterprise: null,
  users: [],
  fusaOrganizations: [],
  workspaces: [],
  projects: []
};

export interface GlobalStore {
  accessToken: string;
  userClass: FusawareUser | null;
  enterprise: Enterprise | null;
  users: UserParams[];
  setAccessToken: (accessToken: string) => void;
  setUserClass: (user: FusawareUser) => void;
  // Turns a raw access token into a signed-in session: resolves it against
  // /api/whoami and stores the token with the user it belongs to. Used by embed
  // mode, where the token arrives from the desktop app rather than from a login.
  authenticateWithToken: (accessToken: string) => Promise<AuthenticateResult>;
  // Registered by useAuthSync -- or, in embed mode, by EmbedAuthProvider -- so
  // non-React code (Client) can recover an expired session.
  redirectToLogin: (() => void) | null;
  setRedirectToLogin: (redirectToLogin: () => void) => void;
  toasts: FusaToast[];
  copiedItem: CopiedItem | null;
  addToast: (toast: FusaToast) => void;
  addErrorToast: (message: string) => void;
  removeToast: (toast: FusaToast) => void;
  reset: () => void;
  setCopiedItem: (item: CopiedItem) => void;
  createFile: (request: CreateFileRequest, file: File, refetchSubject: (updatedConsumers: UpdatedParamsAndRefs) => void, onProgress: (percent: number) => void) => Promise<CreateFileResponse>
  replaceFile: (request: CreateFileRequest, file: File, refetchSubject: (updatedConsumers: UpdatedParamsAndRefs) => void, onProgress: (percent: number) => void) => Promise<CreateFileResponse>
  handleNewFileResponse: (response: CreateFileResponse, file: File, refetchSubject: (updatedConsumers: UpdatedParamsAndRefs) => void, onProgress: (percent: number) => void) => Promise<CreateFileResponse>
  deleteFile: (id: string, refetchSubject?: (updatedConsumers: UpdatedParamsAndRefs) => void) => Promise<void>
  getFileSourceUrl: (id: string) => Promise<GetFileDownloadUrlResponse>
  // Uploads the bytes for a file the server created as pending, then confirms them so the file
  // becomes visible. Resolves to the confirm response, or undefined if either step failed.
  // Rejects with an AbortError when `signal` is aborted; callers that pass a signal must handle it.
  uploadFile: (fileId: string, uploadUrl: string, file: File, onProgress: (percent: number) => void, signal?: AbortSignal) => Promise<ConfirmFileUploadResponse | undefined>
  downloadFileById: (file: FileModel) => Promise<void>
  fetchOrCreateEnterprise: () => Promise<void>
  fetchOrCreateUser: () => Promise<void>
  fetchUsers: () => Promise<void>
}

export const useGlobalStore = create<GlobalStore>((set, get) => ({
  reset: () => set({ ...initialState }),
  ...initialState,
  setAccessToken: (accessToken: string) => set({ accessToken }),
  setUserClass: (user: FusawareUser) => set({ userClass: user }),
  authenticateWithToken: async (accessToken: string) => {
    const result = await new WhoamiController().fetch(accessToken);

    if (result.status === "ok") {
      // One update rather than two setters: nothing should ever observe a token
      // without the user it belongs to, or the other way round.
      set({ userClass: toFusawareUser(result.user), accessToken });
      return "ok";
    }

    if (result.status === "unauthorized") {
      // Drop both. A stale token left behind would be picked up and sent by the
      // next request, and a user without a token reads as signed in.
      set({ userClass: null, accessToken: '' });
      return "unauthorized";
    }

    return "error";
  },
  setRedirectToLogin: (redirectToLogin: () => void) => set({ redirectToLogin }),
  addToast: (toast: FusaToast) => {
    const { toasts } = useGlobalStore.getState();
    const isDuplicate = toasts.some(t => t.message === toast.message);
    if (isDuplicate) {
      return;
    }
    set({ toasts: [...toasts, toast] });
  },
  addErrorToast: (message: string) => {
    get().addToast({
      message: message,
      type: FusaToastType.ERROR,
      header: I18n.get('error'),
    });
  },
  removeToast: (toast: FusaToast) => {
    const { toasts } = useGlobalStore.getState();
    set({ toasts: toasts.filter(t => t.message !== toast.message) });
  },
  setCopiedItem: (copiedItem: CopiedItem) => { set({ copiedItem }); },
  createFile: async (request, file, refetchSubject, onProgress) => {
    const response = await new FileController().create(request);
    return get().handleNewFileResponse(response, file, refetchSubject, onProgress);
  },
  replaceFile: async (request, file, refetchSubject, onProgress) => {
    const response = await new FileController().replace(request);
    return get().handleNewFileResponse(response, file, refetchSubject, onProgress);
  },
  handleNewFileResponse: async (response, file, refetchSubject, onProgress) => {
    if (response.status === 201) {
      const confirmed = await get().uploadFile(response.id, response.uploadUrl, file, onProgress);

      if (!confirmed) {
        await get().deleteFile(response.id, refetchSubject);
      } else {
        if (refetchSubject) {
          refetchSubject(confirmed);
        }
      }
    }
    return response;
  },
  deleteFile: async (id, refetchSubject) => {
    const response = await new FileController().delete(id);
    if (response.status === 200 && refetchSubject) {
      refetchSubject(response);
    }
  },
  getFileSourceUrl: async (id: string) => {
    return new FileController().getS3Url(id);
  }, 
  uploadFile: async (fileId, uploadUrl, file, onProgress, signal) => {
    const controller = new FileController();
    const s3Response = await controller.uploadToS3(uploadUrl, file, onProgress, signal);
    if (!s3Response.ok) {
      get().addErrorToast(I18n.get('uploadFailed'));
      return undefined;
    }
    const confirmed = await controller.confirmUpload(fileId);
    return confirmed.status === 200 ? confirmed : undefined;
  },
  downloadFileById: async (file) => {
    const response = await get().getFileSourceUrl(file.id);
    if (response.status === 200) {
      try {
        await downloadFileFromS3(response.downloadUrl, file.fileName);
      } catch (error) {
        get().addErrorToast(I18n.get('failedToDownloadFile', { fileName: file.fileName }));
      }
    }
  },
  fetchOrCreateEnterprise: async () => {
    const response = await new EnterpriseController().readByUserPropelOrgIdOrCreate();
    if (response.enterprise) {
      set({ enterprise: new Enterprise(response.enterprise) });
    }
  },
  fetchOrCreateUser: async () => {
    await new UserController().readByEmailOrCreate();
  },
  fetchUsers: async () => {
    const response = await new UserController().readAllByPropelOrgId();
    if (response.users) {
      set({ users: response.users });
    }
  },
}));

